/**
 * Scout Lesson Viewer Component
 * Renders panel-based lessons with Scout the Fox
 */

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, Award, BookOpen } from 'lucide-react'
import ScoutAudioPlayer from './ScoutAudioPlayer'

export default function ScoutLessonViewer({ lesson, onComplete }) {
  const [currentPanel, setCurrentPanel] = useState(0)
  const [completedPanels, setCompletedPanels] = useState(new Set())

  const panel = lesson.panels[currentPanel]
  const isLastPanel = currentPanel === lesson.panels.length - 1
  const isFirstPanel = currentPanel === 0

  const handleNext = () => {
    if (!isLastPanel) {
      setCompletedPanels(prev => new Set([...prev, currentPanel]))
      setCurrentPanel(prev => prev + 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      // Mark lesson as complete
      setCompletedPanels(prev => new Set([...prev, currentPanel]))
      if (onComplete) {
        onComplete(lesson)
      }
    }
  }

  const handlePrevious = () => {
    if (!isFirstPanel) {
      setCurrentPanel(prev => prev - 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const goToPanel = (index) => {
    setCurrentPanel(index)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Lesson Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
          <BookOpen className="w-4 h-4" />
          <span className="capitalize">{lesson.difficulty}</span>
          <span>•</span>
          <span>{lesson.est_minutes} min</span>
          <span>•</span>
          <span>{lesson.xp} XP</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{lesson.title}</h1>

        {/* Progress Bar */}
        <div className="flex gap-1">
          {lesson.panels.map((_, index) => (
            <button
              key={index}
              onClick={() => index <= Math.max(...completedPanels, 0) && goToPanel(index)}
              className={`flex-1 h-2 rounded-full transition-all ${
                index === currentPanel
                  ? 'bg-orange-500'
                  : completedPanels.has(index)
                  ? 'bg-green-400'
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <div className="text-sm text-gray-600 mt-2">
          Panel {currentPanel + 1} of {lesson.panels.length}
        </div>
      </div>

      {/* Panel Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentPanel}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="mb-8"
        >
          {renderPanel(panel, lesson)}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-6 border-t">
        <button
          onClick={handlePrevious}
          disabled={isFirstPanel}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            isFirstPanel
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
          Previous
        </button>

        <button
          onClick={handleNext}
          className="flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors shadow-md"
        >
          {isLastPanel ? (
            <>
              Complete Lesson
              <Award className="w-5 h-5" />
            </>
          ) : (
            <>
              Continue
              <ChevronRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  )
}

// Panel Rendering Functions
function renderPanel(panel, lesson) {
  switch (panel.type) {
    case 'intro':
      return <IntroPanel panel={panel} lesson={lesson} />
    case 'explain':
      return <ExplainPanel panel={panel} />
    case 'diagram':
      return <DiagramPanel panel={panel} />
    case 'carousel':
      return <CarouselPanel panel={panel} />
    case 'scout_tip':
      return <ScoutTipPanel panel={panel} />
    case 'example':
      return <ExamplePanel panel={panel} />
    case 'summary':
      return <SummaryPanel panel={panel} lesson={lesson} />
    default:
      return <div className="p-4 bg-red-100 text-red-700 rounded-lg">Unknown panel type: {panel.type}</div>
  }
}

// Individual Panel Components
function IntroPanel({ panel, lesson }) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">{panel.title}</h2>
        <p className="text-xl text-gray-600">{panel.objective}</p>
      </div>

      {lesson.objectives && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-3">What You'll Learn:</h3>
          <ul className="space-y-2">
            {lesson.objectives.map((obj, i) => (
              <li key={i} className="flex items-start gap-2 text-gray-700">
                <span className="text-orange-500 mt-1">✓</span>
                <span>{obj}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {panel.scout_message && (
        <ScoutAudioPlayer
          audioUrl={panel.scout_message.audio_url}
          scoutAvatar={`/scout/${lesson.scout_outfit || 'default'}-${panel.scout_message.expression}.png`}
          expression={panel.scout_message.expression}
        />
      )}
    </div>
  )
}

function ExplainPanel({ panel }) {
  return (
    <div className="prose prose-lg max-w-none">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">{panel.heading}</h2>
      {panel.body.map((paragraph, i) => (
        <p key={i} className="text-gray-700 mb-4 leading-relaxed">{paragraph}</p>
      ))}
    </div>
  )
}

function DiagramPanel({ panel }) {
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-200">
        <img
          src={panel.diagram_spec.generated_image_url}
          alt={panel.diagram_spec.kind}
          className="w-full"
        />
      </div>
      {panel.caption && (
        <p className="text-center text-gray-600 italic">{panel.caption}</p>
      )}
    </div>
  )
}

function CarouselPanel({ panel }) {
  const [activeCard, setActiveCard] = useState(0)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {panel.items.map((item, i) => (
          <motion.div
            key={item.id}
            onClick={() => setActiveCard(i)}
            className={`p-6 rounded-xl cursor-pointer transition-all ${
              activeCard === i
                ? 'bg-orange-100 border-2 border-orange-500 shadow-lg'
                : 'bg-white border-2 border-gray-200 hover:border-orange-300'
            }`}
            whileHover={{ y: -4 }}
          >
            <h3 className="font-bold text-lg text-gray-900 mb-2">{item.title}</h3>
            <p className="text-gray-600">{item.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

function ScoutTipPanel({ panel }) {
  return (
    <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-8 rounded-2xl border-2 border-orange-300 shadow-lg">
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
          💡
        </div>
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Scout's Pro Tip</h3>
          <p className="text-lg text-gray-700">{panel.scout_message.text}</p>
        </div>
      </div>

      {panel.scout_message.audio_url && (
        <ScoutAudioPlayer
          audioUrl={panel.scout_message.audio_url}
          scoutAvatar={`/scout/default-${panel.scout_message.expression}.png`}
          expression={panel.scout_message.expression}
        />
      )}
    </div>
  )
}

function ExamplePanel({ panel }) {
  const [showSolution, setShowSolution] = useState(false)

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200">
        <h3 className="font-bold text-xl text-gray-900 mb-3">Survival Scenario</h3>
        <p className="text-gray-700 mb-4">{panel.scenario}</p>
        <p className="text-orange-600 font-medium">{panel.scout_question}</p>
      </div>

      {!showSolution ? (
        <button
          onClick={() => setShowSolution(true)}
          className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
        >
          Show Solution
        </button>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 p-6 rounded-xl border-2 border-green-300"
        >
          <h4 className="font-bold text-gray-900 mb-2">Solution:</h4>
          <p className="text-gray-700 mb-4">{panel.explanation}</p>
          <div className="flex items-start gap-2 text-green-700 font-medium">
            <span>✓</span>
            <span>{panel.scout_approval}</span>
          </div>
        </motion.div>
      )}
    </div>
  )
}

function SummaryPanel({ panel, lesson }) {
  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-gray-900">Key Takeaways</h2>

      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <ul className="space-y-3">
          {panel.bullets.map((bullet, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                {i + 1}
              </span>
              <span className="text-gray-700 pt-0.5">{bullet}</span>
            </li>
          ))}
        </ul>
      </div>

      {panel.scout_message && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-xl border-2 border-orange-200">
          <p className="text-lg text-gray-700 mb-4">{panel.scout_message.text}</p>
          {panel.scout_message.audio_url && (
            <ScoutAudioPlayer
              audioUrl={panel.scout_message.audio_url}
              scoutAvatar={`/scout/${lesson.scout_outfit || 'default'}-${panel.scout_message.expression}.png`}
              expression={panel.scout_message.expression}
            />
          )}
        </div>
      )}

      {panel.next_lesson_slug && (
        <div className="text-center">
          <p className="text-gray-600 mb-2">Ready for more?</p>
          <a
            href={`/lessons/${panel.next_lesson_slug}`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
          >
            Next Lesson
            <ChevronRight className="w-5 h-5" />
          </a>
        </div>
      )}
    </div>
  )
}
